import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Alert,
  FormControl, InputLabel, Select as MuiSelect, MenuItem, Box,
} from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { MAX_WEEK } from './useAdminWeek';
import { formatKickoff, teamLabel } from './gameDisplay';

// Confirms moving the selected games (and everyone's picks on them) to another
// week. The move runs server-side in moveGamesToWeek, which re-checks every
// game and moves all of them or none.
function MoveGamesDialog({ open, season, week, games, teams, onClose, onMoved }) {
  const [toWeek, setToWeek] = useState('');
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const current = Number(week);
    setToWeek(current < MAX_WEEK ? current + 1 : current - 1);
    setError('');
  }, [open, week]);

  const handleMove = async () => {
    setIsMoving(true);
    setError('');
    try {
      const moveGamesToWeek = httpsCallable(getFunctions(), 'moveGamesToWeek');
      const result = await moveGamesToWeek({
        season,
        fromWeek: Number(week),
        toWeek: Number(toWeek),
        gameIds: games.map((g) => g.id),
      });
      onMoved({ toWeek: Number(toWeek), ...result.data });
    } catch (err) {
      // The server's message lists every game it refused and why.
      setError(err.message || 'The move failed. Nothing was changed.');
    } finally {
      setIsMoving(false);
    }
  };

  const count = games.length;
  return (
    <Dialog open={open} onClose={() => { if (!isMoving) onClose(); }} fullWidth maxWidth="xs">
      <DialogTitle>{`Move ${count} game${count === 1 ? '' : 's'} from Week ${week}`}</DialogTitle>
      <DialogContent>
        <FormControl size="small" fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel id="move-to-week-label">Move to week</InputLabel>
          <MuiSelect
            labelId="move-to-week-label"
            label="Move to week"
            value={toWeek}
            onChange={(e) => setToWeek(e.target.value)}
          >
            {Array.from({ length: MAX_WEEK }, (_, i) => i + 1)
              .filter((w) => w !== Number(week))
              .map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
          </MuiSelect>
        </FormControl>

        <Box component="ul" sx={{ m: 0, pl: 2.5 }} aria-label="Games to move">
          {games.map((game) => (
            <li key={game.id}>
              <Typography variant="body2">
                {`${teamLabel(teams, game.awayTeam, game.awayRanking)} at ${teamLabel(teams, game.homeTeam, game.homeRanking)}`}
                <Box component="span" sx={{ color: 'text.secondary' }}>{` · ${formatKickoff(game.startTime)}`}</Box>
              </Typography>
            </li>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Picks players already made on these games move with them. Week {toWeek} is then
          put in Auto Sort order, including the games already there.
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isMoving}>Cancel</Button>
        <Button variant="contained" onClick={handleMove} loading={isMoving} disabled={!toWeek || count === 0}>
          {`Move to Week ${toWeek}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MoveGamesDialog;
